import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { LogOut, FlaskConical, ClipboardList, Home, MessageCircle } from "lucide-react";
import { BRAND } from "../lib/constants";
import { useAuth } from "../lib/auth";
import { coachMessagesUnreadCount } from "../lib/api";

export default function BrandHeader() {
  const loc = useLocation();
  const isReport = loc.pathname.includes("/report");
  const { signOut } = useAuth();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const load = () =>
      coachMessagesUnreadCount()
        .then((d) => setUnread(d.total || 0))
        .catch(() => {});
    load();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 60 * 1000);
    return () => clearInterval(id);
  }, [loc.pathname]);

  if (isReport) return null; // report has its own branded banner for print

  const linkBase =
    "inline-flex items-center gap-1.5 text-xs tracking-[0.22em] uppercase transition-colors";
  const linkInactive = "text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)]";
  const linkActive = "text-[var(--tir-burgundy)] font-semibold";

  return (
    <header
      className="no-print w-full border-b border-[var(--tir-border)] bg-[var(--tir-ivory)]/90 backdrop-blur-sm sticky top-0 z-40"
      data-testid="brand-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-3 group" data-testid="brand-home-link">
          <img
            src={BRAND.logoUrl}
            alt={`${BRAND.name} logo`}
            className="h-10 w-auto object-contain"
            style={{ filter: "invert(1)" }}
            data-testid="brand-logo"
          />
          <div className="leading-tight hidden sm:block">
            <div className="font-serif-display text-lg text-[var(--tir-ink)]">{BRAND.name}</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-muted)]">
              Studio
            </div>
          </div>
        </Link>
        <nav className="flex items-center gap-5 sm:gap-7">
          <NavLink
            to="/dashboard"
            end
            data-testid="nav-home"
            className={({ isActive }) =>
              `${linkBase} ${isActive ? linkActive : linkInactive}`
            }
            title="Home"
          >
            <Home size={13} />
            <span className="hidden sm:inline">Home</span>
          </NavLink>
          <NavLink
            to="/lactate"
            data-testid="nav-lactate"
            className={({ isActive }) =>
              `${linkBase} ${isActive ? linkActive : linkInactive}`
            }
          >
            <FlaskConical size={13} />
            <span>Lactate</span>
          </NavLink>
          <NavLink
            to="/coaching"
            data-testid="nav-coaching"
            className={() =>
              `${linkBase} ${
                loc.pathname.startsWith("/coaching") && !loc.pathname.startsWith("/coaching/messages")
                  ? linkActive
                  : linkInactive
              }`
            }
          >
            <ClipboardList size={13} />
            <span>Coaching</span>
          </NavLink>
          <NavLink
            to="/coaching/messages"
            data-testid="nav-messages"
            className={({ isActive }) =>
              `${linkBase} ${isActive ? linkActive : linkInactive}`
            }
          >
            <span className="relative">
              <MessageCircle size={13} />
              {unread > 0 && (
                <span
                  className="absolute -top-1.5 -right-2 min-w-[14px] h-[14px] px-0.5 bg-[var(--tir-burgundy)] text-white text-[8px] font-bold rounded-full flex items-center justify-center leading-none"
                  data-testid="nav-messages-badge"
                >
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </span>
            <span className="hidden sm:inline">Messages</span>
          </NavLink>
          <button
            type="button"
            onClick={signOut}
            data-testid="logout-btn"
            className="inline-flex items-center gap-1.5 text-xs tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] transition-colors"
            title="Sign out"
          >
            <LogOut size={13} /> <span className="hidden sm:inline">Sign out</span>
          </button>
        </nav>
      </div>
    </header>
  );
}
